import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Camera, Trash2, Loader2, Check, ZoomIn } from 'lucide-react';
import api from '../utils/api';
import { uploadFileChunked } from '../utils/chunkedUpload';
import { ehArquivoDeImagem } from '../utils/rdoUtils';
import UserAvatar from './common/UserAvatar';

const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const VP = 256;   // diâmetro do recortador (px na tela)
const OUT = 512;  // resolução da imagem exportada (px)

/**
 * Modal de perfil — permite ao usuário logado trocar/remover a própria foto.
 * Ao escolher a imagem, abre um RECORTADOR (arrasta p/ posicionar + zoom) e salva
 * a foto já enquadrada no círculo (canvas → JPEG) → upload R2 → PUT /auth/me/foto.
 */
const PerfilModal = ({ open, onClose, user, onUpdated }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // ----- estado do recortador -----
  const [cropSrc, setCropSrc] = useState(null);  // data URL da imagem escolhida
  const imgRef = useRef(null);                    // <img> do recorte (naturalW/H)
  const [ready, setReady] = useState(false);      // imagem carregada (temos dimensões)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  // ANTES do `if (!open) return null` — hook depois de return antecipado quebra a ordem dos hooks
  // (o build reprova com `react-hooks/rules-of-hooks`). Foi onde eu o pus da primeira vez.
  const [convertendo, setConvertendo] = useState(false);

  if (!open) return null;

  const roleLabel = user?.role === 'user' ? 'Colaborador' : 'Administrador';

  const resetCrop = () => { setCropSrc(null); setReady(false); setZoom(1); setPan({ x: 0, y: 0 }); drag.current = null; };

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
    if (!file) return;
    // HEIC do iPhone chega com MIME VAZIO — `''.startsWith('image/')` é falso, e a foto era
    // recusada com "Selecione um arquivo de imagem", culpando o usuário por um formato que ele
    // não escolheu. `ehArquivoDeImagem` é a régua única do app.
    if (!ehArquivoDeImagem(file)) { toast.error('Selecione um arquivo de imagem'); return; }
    if (file.size > MAX_BYTES) { toast.error('Imagem muito grande (máx. 5MB)'); return; }

    // ════════════════════════════════════════════════════════════════════════════════════════
    // A ORDEM SE INVERTE PARA O HEIC: sobe primeiro, recorta depois
    // ════════════════════════════════════════════════════════════════════════════════════════
    // Esta tela RECORTA antes de enviar, e recortar exige que o NAVEGADOR desenhe a imagem — o
    // Chrome não decodifica HEIC, então o recorte abriria em branco. A conversão do servidor
    // (`backend/services/imagem_heic.py`) acontece no upload, ou seja DEPOIS do recorte.
    //
    // Das três saídas possíveis, esta é a que não custa nada ao colaborador: uma biblioteca de
    // decodificação no cliente pesaria ~1MB no pacote de um app usado em dados móveis no campo, e
    // recusar seria manter o defeito com uma mensagem mais educada. Aqui o arquivo sobe, o
    // servidor devolve um JPEG, e o recorte trabalha em cima dele — que é o mesmo recorte de
    // sempre, sobre bytes que o navegador sabe desenhar.
    //
    // O JPEG/PNG de sempre NÃO passa por aqui: continua indo direto para o recorte, sem uma volta
    // ao servidor que ninguém pediu.
    const nome = (file.name || '').toLowerCase();
    const ehHeic = /\.(heic|heif)$/.test(nome)
      || ['image/heic', 'image/heif'].includes((file.type || '').toLowerCase());
    if (ehHeic) {
      setConvertendo(true);
      try {
        const up = await uploadFileChunked(file, undefined, 'imagem');
        if (!up?.url) throw new Error('upload sem url');
        resetCrop();
        setCropSrc(up.url);
      } catch (err) {
        console.error('Erro ao converter HEIC:', err);
        toast.error('Não consegui preparar esta foto. Tente novamente ou envie em JPEG.');
      } finally {
        setConvertendo(false);
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => { resetCrop(); setCropSrc(reader.result); };
    reader.onerror = () => toast.error('Não foi possível ler a imagem');
    reader.readAsDataURL(file);
  };

  // Escala p/ a imagem COBRIR o círculo no zoom 1; aplica zoom por cima.
  // As helpers aceitam um `z` explícito porque setZoom() é assíncrono: ao mudar o
  // zoom precisamos re-clampar o pan com o zoom NOVO, não com o do render anterior
  // (senão um salto de zoom deixa o pan fora dos limites → borda branca no recorte).
  const _bs = (z = zoom) => {
    const im = imgRef.current;
    if (!im || !im.naturalWidth) return 1;
    return Math.max(VP / im.naturalWidth, VP / im.naturalHeight) * z;
  };
  const _renderSize = (z = zoom) => {
    const im = imgRef.current; const bs = _bs(z);
    return { w: (im?.naturalWidth || VP) * bs, h: (im?.naturalHeight || VP) * bs };
  };
  const _clamp = (p, z = zoom) => {
    const { w, h } = _renderSize(z);
    const mx = Math.max(0, (w - VP) / 2), my = Math.max(0, (h - VP) / 2);
    return { x: Math.min(mx, Math.max(-mx, p.x)), y: Math.min(my, Math.max(-my, p.y)) };
  };

  const onImgLoad = () => { setReady(true); setPan((p) => _clamp(p)); };
  const onZoom = (z) => { setZoom(z); setPan((p) => _clamp(p, z)); };

  const onPointerDown = (e) => {
    if (!ready) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPan(_clamp({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) }));
  };
  const onPointerUp = () => { drag.current = null; };

  const imgStyle = () => {
    if (!ready) return { position: 'absolute', opacity: 0 };
    const { w, h } = _renderSize();
    return { position: 'absolute', width: w, height: h, maxWidth: 'none', left: VP / 2 + pan.x - w / 2, top: VP / 2 + pan.y - h / 2 };
  };

  const cropAndUpload = async () => {
    const im = imgRef.current;
    if (!im || !im.naturalWidth) return;
    setBusy(true);
    try {
      const bs = _bs();
      const rw = im.naturalWidth * bs, rh = im.naturalHeight * bs;
      const left = VP / 2 + pan.x - rw / 2, top = VP / 2 + pan.y - rh / 2;
      const sSize = VP / bs;               // lado do recorte em px naturais da imagem
      const sx = -left / bs, sy = -top / bs;
      const canvas = document.createElement('canvas');
      canvas.width = OUT; canvas.height = OUT;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, OUT, OUT);
      ctx.drawImage(im, sx, sy, sSize, sSize, 0, 0, OUT, OUT);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('falha ao gerar imagem');
      const cropped = new File([blob], 'perfil.jpg', { type: 'image/jpeg' });
      const up = await uploadFileChunked(cropped, undefined, 'imagem');
      const url = up?.url;
      if (!url) throw new Error('upload sem url');
      const res = await api.put('/auth/me/foto', { foto_url: url });
      onUpdated?.(res.data);
      toast.success('Foto de perfil atualizada');
      resetCrop();
      onClose?.();
    } catch (err) {
      console.error('Erro ao atualizar foto:', err);
      toast.error('Não foi possível atualizar a foto');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      const res = await api.put('/auth/me/foto', { foto_url: null });
      onUpdated?.(res.data);
      toast.success('Foto removida');
      onClose?.();
    } catch (err) {
      console.error('Erro ao remover foto:', err);
      toast.error('Não foi possível remover a foto');
    } finally {
      setBusy(false);
    }
  };

  const podeFechar = !busy && !cropSrc;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={podeFechar ? onClose : undefined}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={cropSrc ? resetCrop : onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
          title={cropSrc ? 'Cancelar' : 'Fechar'}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold text-slate-800 mb-5">{cropSrc ? 'Ajustar foto' : 'Meu perfil'}</h2>

        {cropSrc ? (
          /* ---------- RECORTADOR ---------- */
          <div className="flex flex-col items-center gap-4">
            <div
              className="relative rounded-full overflow-hidden bg-slate-100 touch-none select-none cursor-move ring-2 ring-slate-100"
              style={{ width: VP, height: VP }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              <img ref={imgRef} src={cropSrc} alt="" draggable={false} onLoad={onImgLoad}
                onError={() => { toast.error('Não foi possível carregar a imagem'); resetCrop(); }} style={imgStyle()} />
              {!ready && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
            </div>
            <div className="w-full flex items-center gap-2">
              <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => onZoom(parseFloat(e.target.value))} disabled={!ready} className="flex-1 accent-primary" data-testid="perfil-zoom" />
            </div>
            <p className="text-xxs text-slate-400 text-center">Arraste para posicionar · use o controle para dar zoom</p>
            <div className="w-full flex gap-2">
              <button
                onClick={resetCrop}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={cropAndUpload}
                disabled={busy || !ready}
                data-testid="perfil-salvar-foto"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Salvar
              </button>
            </div>
          </div>
        ) : (
          /* ---------- VISÃO NORMAL ---------- */
          <>
            <div className="flex flex-col items-center gap-3">
              <UserAvatar user={user} size={96} className="ring-2 ring-slate-100" />
              <div className="text-center">
                <p className="font-medium text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
                <p className="text-xxs text-slate-400 mt-0.5">{roleLabel}</p>
              </div>
            </div>

            {/* `.heic,.heif` EXPLICITOS: `image/*` cobre o iPhone, mas nao o arquivo HEIC vindo
                do gerenciador de arquivos em alguns aparelhos — onde o SO nao registra o formato
                como imagem, `image/*` sozinho o esconde do seletor. */}
            <input ref={inputRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={handlePick} data-testid="perfil-foto-input" />

            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => inputRef.current?.click()}
                disabled={busy || convertendo}
                data-testid="perfil-alterar-foto"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {(busy || convertendo) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {/* A conversao do HEIC passa pelo servidor e leva alguns segundos no celular. Sem
                    dizer isso, o botao parece travado e a pessoa toca de novo. */}
                {convertendo ? 'Preparando a foto...' : (user?.foto_url ? 'Trocar foto' : 'Enviar foto')}
              </button>
              {user?.foto_url && (
                <button
                  onClick={handleRemove}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" />
                  Remover foto
                </button>
              )}
            </div>

            <p className="text-xxs text-slate-400 text-center mt-4">JPG, PNG ou GIF · máx. 5MB</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PerfilModal;
